// Plan selection after signup: Freemium (7-day), Monthly, Yearly (payment coming later)
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../config/apiConfig";

const BRAND = {
  bg: "#0a0a0f",
  panel: "#1a1a24",
  border: "#2a2a3a",
  text: "#e8e8f0",
  textSecondary: "#b8b8c8",
  accent: "#6366f1",
  accentHover: "#818cf8",
  muted: "#888898",
};

function getAuthHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function PlanSelectPage() {
  const [loading, setLoading] = useState(null); // "freemium" | "monthly" | "yearly"
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const selectPlan = async (plan) => {
    if (plan === "monthly" || plan === "yearly") {
      setError("Monthly and Yearly plans are coming soon. Payment will be available shortly.");
      return;
    }
    setError("");
    setLoading(plan);
    try {
      await axios.post(`${API_BASE_URL}/auth/select-plan`, { plan }, { headers: getAuthHeaders() });
      const user = JSON.parse(localStorage.getItem("authUser") || "{}");
      user.plan = plan;
      localStorage.setItem("authUser", JSON.stringify(user));
      navigate("/", { replace: true });
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const siteFont = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Inter', 'Helvetica Neue', sans-serif";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: BRAND.bg,
        backgroundImage: "radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.05) 0%, transparent 50%)",
        color: BRAND.text,
        padding: 24,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: siteFont,
      }}
    >
      <Link to="/" style={{ position: "absolute", top: 20, left: 20, color: BRAND.textSecondary, fontSize: 14, textDecoration: "none" }}>
        ← Back to home
      </Link>
      <div style={{ maxWidth: 900, width: "100%", textAlign: "center" }}>
        <h1 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 700 }}>Choose your plan</h1>
        <p style={{ margin: "0 0 32px", color: BRAND.textSecondary, fontSize: 16 }}>Start with a free trial or subscribe when payment is available.</p>
        {error && (
          <div style={{ marginBottom: 16, padding: 12, background: "rgba(239, 68, 68, 0.15)", border: "1px solid #ef4444", borderRadius: 8, color: "#ef4444", fontSize: 14 }}>
            {error}
          </div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "center" }}>
          {/* Freemium */}
          <div
            style={{
              background: BRAND.panel,
              border: `2px solid ${BRAND.accent}`,
              borderRadius: 16,
              padding: 32,
              minWidth: 260,
              flex: "1 1 260px",
              maxWidth: 280,
            }}
          >
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600 }}>Freemium</h3>
            <p style={{ margin: "0 0 8px", fontSize: 24, fontWeight: 800, color: BRAND.accent }}>Free</p>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: BRAND.textSecondary }}>7-day free trial. Full access to charts, screener, and portfolio.</p>
            <button
              onClick={() => selectPlan("freemium")}
              disabled={loading !== null}
              style={{
                width: "100%",
                padding: "12px 24px",
                background: BRAND.accent,
                color: BRAND.text,
                border: "none",
                borderRadius: 12,
                fontWeight: 600,
                fontSize: 14,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading === "freemium" ? 0.7 : 1,
              }}
            >
              {loading === "freemium" ? "Activating..." : "Start free trial"}
            </button>
          </div>
          {/* Monthly */}
          <div
            style={{
              background: BRAND.panel,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 16,
              padding: 32,
              minWidth: 260,
              flex: "1 1 260px",
              maxWidth: 280,
              opacity: 0.9,
            }}
          >
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600 }}>Monthly</h3>
            <p style={{ margin: "0 0 8px", fontSize: 24, fontWeight: 800, color: BRAND.textSecondary }}>$19.99<span style={{ fontSize: 14, fontWeight: 500 }}>/month</span></p>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: BRAND.textSecondary }}>Billed monthly. Payment page coming soon.</p>
            <button
              onClick={() => selectPlan("monthly")}
              style={{
                width: "100%",
                padding: "12px 24px",
                background: BRAND.border,
                color: BRAND.muted,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 12,
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Coming soon
            </button>
          </div>
          {/* Yearly */}
          <div
            style={{
              background: BRAND.panel,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 16,
              padding: 32,
              minWidth: 260,
              flex: "1 1 260px",
              maxWidth: 280,
              opacity: 0.9,
            }}
          >
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600 }}>Yearly</h3>
            <p style={{ margin: "0 0 8px", fontSize: 24, fontWeight: 800, color: BRAND.textSecondary }}>$14.99<span style={{ fontSize: 14, fontWeight: 500 }}>/month</span></p>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: BRAND.textSecondary }}>Billed annually. Payment page coming soon.</p>
            <button
              onClick={() => selectPlan("yearly")}
              style={{
                width: "100%",
                padding: "12px 24px",
                background: BRAND.border,
                color: BRAND.muted,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 12,
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Coming soon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
